import React from "react";

import { ContentLoader } from "@common/components/util/ContentLoader";
import { Loading } from "@common/components/util/Loading";
import { Workbench } from "@common/components/Workbench";
import { RawZones } from "./useInitialState";
import { WorkbenchInit } from "./WorkbenchInit";
import { Help } from "./Help";
import { ChildrenArgs } from "../../../common/components/Workbench/types";

type Props = Readonly<{
  review: boolean;
}>;

export const ReadingWorkbench = ({ review }: Props) => {
  return (
    <Workbench
      blockingStep="reading"
      docs={<Help/>}
      loadRoles={[
        "zoning-pagesmeta-json",
        "zoning-pagehr-png",
        "reading-predicted-json",
        "reading-reviewed-json",
      ]}
      review={review}
      settings={undefined}
      workbenchTitle="Reading.ml"
    >
      {({ 
        actions: { saveContent, showMessage, checkinJob }, 
        auth, 
        contents,
        job
      } : ChildrenArgs) => {
        const pages = contents.find(c => c.role === "zoning-pagesmeta-json");
        if (!pages) {
          throw new Error("Could not find page count metadata.");
        }
        const reviewedZones = contents.find(c => c.role === "reading-reviewed-json");
        if (!reviewedZones) {
          throw new Error("Could not find reviewed zones.");
        } 
        const predictedZones = contents.find(c => c.role === "reading-predicted-json");
        if (!predictedZones) {
          throw new Error("Could not find predicted zones.");
        }
        const pagePngs = contents.filter(c => c.role === "zoning-pagehr-png");
        if (pagePngs.length < 1) {
          throw new Error("Could not find page images.");
        }
        return (
          <ContentLoader auth={auth.authString} content={[pages, predictedZones, reviewedZones]}>
            {results => {
              if (results.ready) {
                const pageMetaContent = results.contents.find(
                  c => c.role === "zoning-pagesmeta-json",
                )?.contents;
                const predictedZonesContent = results.contents.find(
                  c => c.role === "reading-predicted-json",
                )?.contents;
                const zonesContent = results.contents.find(c => c.role === "reading-reviewed-json")
                  ?.contents;
                const pageUris = pagePngs.map(
                  p => `${p.uri}?token=${encodeURIComponent(auth.token)}`,
                );

                const onReset = async () => {
                  await saveContent(
                    "reading-reviewed-json",
                    1,
                    JSON.stringify(predictedZonesContent),
                    false,
                  );
                  window.location.reload();
                };
                const onSave = async (blocks: RawZones) => {
                  await saveContent(
                    "reading-reviewed-json",
                    1,
                    JSON.stringify(blocks),
                    false,
                  );
                };
                return (
                  <WorkbenchInit
                    pageMeta={pageMetaContent}
                    pageUris={pageUris}
                    onReset={onReset}
                    review={review}
                    saveContent={onSave}
                    showMessage={showMessage}
                    submit={checkinJob}
                    zones={zonesContent}
                    job={job}
                  />
                );
              }
              return <Loading progress={results.progress} text="Loading job details" />;
            }}
          </ContentLoader>
        );
      }}
    </Workbench>
  );
};
ReadingWorkbench.displayName = "ContentWorkbench";
